import { UserRole } from '@/types';

/**
 * TCX-713: whether the signed-in user is on the restricted $99 TCX Connect
 * plan (cannot start new conversations, cannot book meetings). Read from the
 * profile's subscriptionInfo — the backend derives it from the live Stripe
 * price metadata with the same helper SubscriptionGuard enforces with, so FE
 * and BE can't disagree on policy. Fails OPEN when subscriptionInfo is absent
 * (profile still loading, or the backend's Stripe fetch hiccupped and nulled
 * it): the affordances render normally and the backend 403s stay the
 * enforcement backstop.
 */
export const isConnectTier = (user?: {
  subscriptionInfo?: { tier?: string | null } | null;
}): boolean => user?.subscriptionInfo?.tier === 'connect';

/**
 * TCX-839: whether the Subscription page should offer the premium "Upgrade"
 * plan tiles. Franchisor + live Connect tier + no request already pending
 * (upgrade or cancellation — the two are mutually exclusive, and the backend
 * also enforces this server-side). Unlike isConnectTier's fail-open, this
 * fails CLOSED when subscriptionInfo is absent — no tier signal means no
 * upgrade offer to render. The page's scheduled-to-cancel state needs no
 * check here: it renders the card-only Cancelled view before the active
 * branch where the tiles live.
 */
export const canRequestUpgrade = (user?: {
  role?: UserRole | string;
  subscriptionInfo?: { tier?: string | null } | null;
  upgradeRequested?: boolean;
  cancellationRequested?: boolean;
}): boolean =>
  user?.role === UserRole.FRANCHISEE &&
  isConnectTier(user) &&
  !user?.upgradeRequested &&
  !user?.cancellationRequested;

/**
 * TCX-917: the statuses that mean a subscription is currently live — i.e.
 * actively paying for access, not merely on file. Shared by
 * isPaymentActivated below and the promo pop-up's `subscription-inactive`
 * condition (packages/website/src/components/popup/promoPopupConditions.ts)
 * so the two checks can't drift out of sync the way they had before.
 */
export const LIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'grace']);

export const isLiveSubscriptionStatus = (status?: string | null): boolean =>
  LIVE_SUBSCRIPTION_STATUSES.has(status ?? '');

export const isPaymentActivated = (
  role: UserRole | undefined,
  subscriptionStatus: string | undefined,
  isFranchableV2: boolean = false,
): boolean => {
  return isFranchableV2
    ? (
        role === UserRole.CONSULTANT ||
        (role === UserRole.FRANCHISEE && isLiveSubscriptionStatus(subscriptionStatus))
      )
    : true;
};
